import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, setAccessToken } from "./client";

export interface AuthTenant { id: string; name: string; cnpj: string }

export interface AuthUser {
  sub: string;
  email: string;
  cpf: string;
  name: string;
  isMasterAdmin: boolean;
  selectedTenantId: string | null;
  /** Instituições onde o usuário é admin local (gating de UI; backend recheca). */
  adminInstitutionIds: string[];
}

export interface SystemEntry { key: string; label: string; basePath: string; baseUrl?: string }

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  tenants: AuthTenant[];
  /** Sistemas (modules) que o usuário tem acesso, vindo do claim `moduleAccess` do JWT. */
  moduleAccess: SystemEntry[];
  /** true depois que o refresh inicial terminou (sucesso ou falha) — UI sabe se mostra login ou conteúdo. */
  ready: boolean;
}

interface SessionResponse {
  accessToken: string;
  expiresIn: number;
  selectedTenantId: string | null;
  tenants: AuthTenant[];
  isMasterAdmin: boolean;
  requiresTenantChoice?: boolean;
}

interface AuthContextValue extends AuthState {
  loginWithPassword: (identifier: string, password: string, selectedTenantId?: string) => Promise<SessionResponse>;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string, selectedTenantId?: string) => Promise<SessionResponse>;
  switchTenant: (tenantId: string) => Promise<void>;
  logout: () => Promise<void>;
  /** "Permanecer conectado": estende a sessão (refresh token de ~30 dias, renovado a cada refresh). */
  staySignedIn: boolean;
  setStaySignedIn: (stay: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Preferência "permanecer conectado", persistida no navegador.
const STAY_KEY = "safrater_stay";
const getStay = (): boolean => { try { return localStorage.getItem(STAY_KEY) === "1"; } catch { return false; } };
const persistStay = (v: boolean) => { try { v ? localStorage.setItem(STAY_KEY, "1") : localStorage.removeItem(STAY_KEY); } catch { /* */ } };

interface JwtPayload {
  sub: string;
  email: string;
  cpf: string;
  name: string;
  isMasterAdmin: boolean;
  selectedTenantId: string | null;
  tenants: AuthTenant[];
  adminInstitutionIds?: string[];
  moduleAccess?: SystemEntry[];
  exp: number;
}

function decode(token: string): JwtPayload | null {
  try {
    const [, payload] = token.split(".");
    // atob() retorna binary string (Latin-1) — JWT é UTF-8, então decodificamos
    // os bytes via TextDecoder para preservar acentos (ex.: "Gestão", "Esperança").
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return JSON.parse(new TextDecoder("utf-8").decode(bytes));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ accessToken: null, user: null, tenants: [], moduleAccess: [], ready: false });
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const scheduleRefresh = useCallback((expSec: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    // Refresh 60s antes do expirar (mínimo 5s).
    const ms = Math.max(5_000, (expSec - 60) * 1000);
    refreshTimer.current = setTimeout(async () => {
      try {
        const res = await api.post<SessionResponse | { error: string }>("/auth/refresh", { staySignedIn: getStay() });
        if ("accessToken" in res.data && res.data.accessToken) {
          applyToken(res.data.accessToken, res.data.expiresIn);
        }
      } catch {
        /* a próxima 401 vai forçar relogin */
      }
    }, ms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyToken = useCallback((token: string | null, expSec?: number) => {
    setAccessToken(token);
    if (refreshTimer.current) { clearTimeout(refreshTimer.current); refreshTimer.current = null; }
    if (!token) {
      setState((s) => ({ ...s, accessToken: null, user: null, tenants: [], moduleAccess: [] }));
      return;
    }
    const payload = decode(token);
    if (!payload) { setState((s) => ({ ...s, accessToken: null, user: null, tenants: [], moduleAccess: [] })); return; }
    setState((s) => ({
      ...s,
      accessToken: token,
      user: {
        sub: payload.sub, email: payload.email, cpf: payload.cpf, name: payload.name,
        isMasterAdmin: payload.isMasterAdmin, selectedTenantId: payload.selectedTenantId,
        adminInstitutionIds: payload.adminInstitutionIds ?? [],
      },
      tenants: payload.tenants ?? [],
      moduleAccess: payload.moduleAccess ?? [],
    }));
    const ttl = expSec ?? Math.max(0, payload.exp - Math.floor(Date.now() / 1000));
    if (ttl > 5) scheduleRefresh(ttl);
  }, [scheduleRefresh]);

  // Reload: tenta refresh via cookie httpOnly. Marca ready=true depois (sucesso ou falha).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post<SessionResponse | { error: string }>("/auth/refresh", { staySignedIn: getStay() });
        if (!cancelled && "accessToken" in res.data && res.data.accessToken) {
          applyToken(res.data.accessToken, res.data.expiresIn);
        }
      } catch {
        /* sem sessão */
      } finally {
        if (!cancelled) setState((s) => ({ ...s, ready: true }));
      }
    })();
    return () => { cancelled = true; if (refreshTimer.current) clearTimeout(refreshTimer.current); };
  }, [applyToken]);

  const loginWithPassword = useCallback(async (identifier: string, password: string, selectedTenantId?: string) => {
    const res = await api.post<SessionResponse>("/auth/login/password", { identifier, password, selectedTenantId, staySignedIn: getStay() });
    applyToken(res.data.accessToken, res.data.expiresIn);
    return res.data;
  }, [applyToken]);

  const sendOtp = useCallback(async (email: string) => {
    await api.post("/auth/otp/send", { email });
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string, selectedTenantId?: string) => {
    const res = await api.post<SessionResponse>("/auth/otp/verify", { email, code, selectedTenantId });
    applyToken(res.data.accessToken, res.data.expiresIn);
    return res.data;
  }, [applyToken]);

  // "Permanecer conectado": persiste a escolha e re-emite o refresh com TTL longo.
  const setStaySignedIn = useCallback(async (stay: boolean) => {
    persistStay(stay);
    try {
      const res = await api.post<SessionResponse | { error: string }>("/auth/refresh", { staySignedIn: stay });
      if ("accessToken" in res.data && res.data.accessToken) applyToken(res.data.accessToken, res.data.expiresIn);
    } catch { /* mantém a sessão atual */ }
  }, [applyToken]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const prev = state.user?.selectedTenantId;
    const res = await api.post<SessionResponse>("/auth/refresh", { selectedTenantId: tenantId, staySignedIn: getStay() });
    if ("accessToken" in res.data && res.data.accessToken) {
      applyToken(res.data.accessToken, res.data.expiresIn);
      if (prev && prev !== tenantId) {
        // Cache do react-query é por queryKey, sem awareness de tenant — limpar tudo
        // garante que listas/detalhes refletam o tenant novo sem refresh manual.
        queryClient.removeQueries();
      }
    }
  }, [applyToken, queryClient, state.user?.selectedTenantId]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* */ }
    persistStay(false);
    applyToken(null);
  }, [applyToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, loginWithPassword, sendOtp, verifyOtp, switchTenant, logout, staySignedIn: getStay(), setStaySignedIn }),
    [state, loginWithPassword, sendOtp, verifyOtp, switchTenant, logout, setStaySignedIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <AuthProvider>");
  return ctx;
}
